import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  CalendarIcon,
  FunnelIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  AdjustmentsHorizontalIcon
} from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';

interface AnalyticsFiltersProps {
  onFiltersChange: (filters: FilterState) => void;
  initialFilters?: Partial<FilterState>;
}

interface FilterState {
  dateRange: {
    start: string;
    end: string;
    preset: string;
  };
  departments: string[];
  metrics: string[];
  detailLevel: string;
  displayType: string;
  currency: string;
}

const AnalyticsFilters: React.FC<AnalyticsFiltersProps> = ({ onFiltersChange, initialFilters }) => {
  const { t, direction } = useLanguage();
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [selectedDateRange, setSelectedDateRange] = useState(initialFilters?.dateRange?.preset || 'last_6_months');
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>(initialFilters?.departments || ['all']);
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>(initialFilters?.metrics || ['revenue', 'expenses', 'profit']);
  const [detailLevel, setDetailLevel] = useState(initialFilters?.detailLevel || 'monthly');
  const [displayType, setDisplayType] = useState(initialFilters?.displayType || 'mixed');
  const [currency, setCurrency] = useState(initialFilters?.currency || 'SAR');
  const [customDateStart, setCustomDateStart] = useState(initialFilters?.dateRange?.start || '');
  const [customDateEnd, setCustomDateEnd] = useState(initialFilters?.dateRange?.end || '');

  const dateRangeOptions = [
    { value: 'today', label: 'اليوم', period: '1 يوم' },
    { value: 'yesterday', label: 'أمس', period: '1 يوم' },
    { value: 'last_7_days', label: 'آخر 7 أيام', period: '7 أيام' },
    { value: 'last_30_days', label: 'آخر 30 يوم', period: '30 يوم' },
    { value: 'last_90_days', label: 'آخر 90 يوم', period: '90 يوم' },
    { value: 'this_month', label: 'هذا الشهر', period: 'الشهر الحالي' },
    { value: 'last_month', label: 'الشهر الماضي', period: 'الشهر السابق' },
    { value: 'this_quarter', label: 'هذا الربع', period: 'الربع الحالي' },
    { value: 'last_quarter', label: 'الربع الماضي', period: 'الربع السابق' },
    { value: 'this_year', label: 'هذا العام', period: 'العام الحالي' },
    { value: 'last_year', label: 'العام الماضي', period: 'العام السابق' },
    { value: 'custom', label: 'فترة مخصصة', period: 'حسب الاختيار' }
  ];

  const departments = [
    { id: 'all', name: 'جميع الأقسام', count: '12 قسم' },
    { id: 'marketing', name: 'التسويق الرقمي', count: '3 فروع' },
    { id: 'sales', name: 'المبيعات', count: '5 فروع' },
    { id: 'operations', name: 'العمليات', count: '4 فروع' },
    { id: 'hr', name: 'الموارد البشرية', count: '2 فروع' },
    { id: 'finance', name: 'المالية والمحاسبة', count: '3 فروع' },
    { id: 'it', name: 'تقنية المعلومات', count: '2 فروع' },
    { id: 'legal', name: 'الشؤون القانونية', count: '1 فرع' },
    { id: 'procurement', name: 'المشتريات', count: '2 فروع' }
  ];

  const metrics = [
    { id: 'revenue', name: 'الإيرادات', icon: '📈', color: 'text-green-600' },
    { id: 'expenses', name: 'المصروفات', icon: '📉', color: 'text-red-600' },
    { id: 'profit', name: 'الأرباح', icon: '💰', color: 'text-yellow-600' },
    { id: 'cashflow', name: 'التدفق النقدي', icon: '💵', color: 'text-blue-600' },
    { id: 'roi', name: 'عائد الاستثمار', icon: '📊', color: 'text-purple-600' },
    { id: 'growth', name: 'معدل النمو', icon: '🚀', color: 'text-indigo-600' },
    { id: 'efficiency', name: 'الكفاءة', icon: '⚡', color: 'text-orange-600' },
    { id: 'liquidity', name: 'السيولة', icon: '💧', color: 'text-cyan-600' }
  ];

  const handleDepartmentToggle = (departmentId: string) => {
    if (departmentId === 'all') {
      setSelectedDepartments(['all']);
    } else {
      const newSelection = selectedDepartments.includes('all')
        ? [departmentId]
        : selectedDepartments.includes(departmentId)
        ? selectedDepartments.filter(id => id !== departmentId)
        : [...selectedDepartments, departmentId];
      
      setSelectedDepartments(newSelection.length === 0 ? ['all'] : newSelection);
    }
  };

  const handleMetricToggle = (metricId: string) => {
    const newSelection = selectedMetrics.includes(metricId)
      ? selectedMetrics.filter(id => id !== metricId)
      : [...selectedMetrics, metricId];
    
    setSelectedMetrics(newSelection.length === 0 ? ['revenue'] : newSelection);
  };

  useEffect(() => {
    const filters: FilterState = {
      dateRange: {
        start: selectedDateRange === 'custom' ? customDateStart : '',
        end: selectedDateRange === 'custom' ? customDateEnd : '',
        preset: selectedDateRange
      },
      departments: selectedDepartments,
      metrics: selectedMetrics,
      detailLevel,
      displayType,
      currency
    };
    onFiltersChange(filters);
  }, [selectedDateRange, selectedDepartments, selectedMetrics, detailLevel, displayType, currency, customDateStart, customDateEnd, onFiltersChange]);

  const handleReset = () => {
    setSelectedDateRange('last_6_months');
    setSelectedDepartments(['all']);
    setSelectedMetrics(['revenue', 'expenses', 'profit']);
    setDetailLevel('monthly');
    setDisplayType('mixed');
    setCurrency('SAR');
    setCustomDateStart('');
    setCustomDateEnd('');
  };

  return (
    <div className="space-y-4">
      {/* Quick Filters Row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4 rtl:space-x-reverse">
          {/* Date Range Filter */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <CalendarIcon className="w-4 h-4 inline ml-1 rtl:ml-0 rtl:mr-1" />
              الفترة الزمنية
            </label>
            <select
              value={selectedDateRange}
              onChange={(e) => setSelectedDateRange(e.target.value)}
              className="block w-48 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {dateRangeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label} ({option.period})
                </option>
              ))}
            </select>
          </div>

          {/* Custom Date Range */}
          {selectedDateRange === 'custom' && (
            <motion.div
              initial={{ opacity: 0, width: 0 }}
              animate={{ opacity: 1, width: 'auto' }}
              className="flex items-center space-x-2 rtl:space-x-reverse"
            >
              <input
                type="date"
                value={customDateStart}
                onChange={(e) => setCustomDateStart(e.target.value)}
                className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              />
              <span className="text-gray-500 dark:text-gray-400">إلى</span>
              <input
                type="date"
                value={customDateEnd}
                onChange={(e) => setCustomDateEnd(e.target.value)}
                className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              />
            </motion.div>
          )}

          {/* Quick Department Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <BuildingOfficeIcon className="w-4 h-4 inline ml-1 rtl:ml-0 rtl:mr-1" />
              الأقسام
            </label>
            <div className="flex items-center space-x-2 rtl:space-x-reverse">
              {departments.slice(0, 4).map((dept) => (
                <button
                  key={dept.id}
                  onClick={() => handleDepartmentToggle(dept.id)}
                  className={`px-3 py-2 text-xs font-medium rounded-lg transition-all duration-200 ${
                    selectedDepartments.includes(dept.id)
                      ? 'bg-blue-500 text-white shadow-md'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {dept.name}
                </button>
              ))}
              <button
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="px-3 py-2 text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
              >
                +{departments.length - 4} المزيد
              </button>
            </div>
          </div>
        </div>

        {/* Advanced Filters Toggle */}
        <button
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
        >
          <AdjustmentsHorizontalIcon className="w-4 h-4 ml-2 rtl:ml-0 rtl:mr-2" />
          فلاتر متقدمة
        </button>
      </div>

      {/* Advanced Filters Panel */}
      {showAdvanced && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700"
        >
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* All Departments */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                <BuildingOfficeIcon className="w-4 h-4 ml-2 rtl:ml-0 rtl:mr-2" />
                اختيار الأقسام
              </h3>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {departments.map((dept) => (
                  <label key={dept.id} className="flex items-center space-x-3 rtl:space-x-reverse">
                    <input
                      type="checkbox"
                      checked={selectedDepartments.includes(dept.id)}
                      onChange={() => handleDepartmentToggle(dept.id)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <div className="flex-1">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {dept.name}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400 block">
                        {dept.count}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Metrics Selection */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                <ChartBarIcon className="w-4 h-4 ml-2 rtl:ml-0 rtl:mr-2" />
                المؤشرات المطلوبة
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {metrics.map((metric) => (
                  <label key={metric.id} className="flex items-center space-x-2 rtl:space-x-reverse p-2 rounded-lg hover:bg-white dark:hover:bg-gray-700 transition-colors">
                    <input
                      type="checkbox"
                      checked={selectedMetrics.includes(metric.id)}
                      onChange={() => handleMetricToggle(metric.id)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-lg">{metric.icon}</span>
                    <span className={`text-sm font-medium ${metric.color}`}>
                      {metric.name}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Additional Filters */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                <FunnelIcon className="w-4 h-4 ml-2 rtl:ml-0 rtl:mr-2" />
                فلاتر إضافية
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    مستوى التفصيل
                  </label>
                  <select
                    value={detailLevel}
                    onChange={(e) => setDetailLevel(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="monthly">شهري</option>
                    <option value="quarterly">ربع سنوي</option>
                    <option value="yearly">سنوي</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    نوع العرض
                  </label>
                  <select
                    value={displayType}
                    onChange={(e) => setDisplayType(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="charts">رسوم بيانية</option>
                    <option value="tables">جداول</option>
                    <option value="mixed">مختلط</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    العملة
                  </label>
                  <select
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="SAR">ريال سعودي (ر.س)</option>
                    <option value="USD">دولار أمريكي ($)</option>
                    <option value="EUR">يورو (€)</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Filter Actions */}
          <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200 dark:border-gray-600">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              تم اختيار: {selectedDepartments.length} أقسام، {selectedMetrics.length} مؤشرات
            </div>
            <div className="flex items-center space-x-3 rtl:space-x-reverse">
              <button
                onClick={handleReset}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                إعادة تعيين
              </button>
              <button
                onClick={() => setShowAdvanced(false)}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 rounded-lg transition-colors"
              >
                تطبيق الفلاتر
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {/* Active Filters Summary */}
      <div className="flex items-center space-x-2 rtl:space-x-reverse">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">الفلاتر النشطة:</span>
        
        {/* Date Range Tag */}
        <span className="inline-flex items-center px-3 py-1 text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-full">
          <CalendarIcon className="w-3 h-3 ml-1 rtl:ml-0 rtl:mr-1" />
          {dateRangeOptions.find(opt => opt.value === selectedDateRange)?.label}
        </span>

        {/* Department Tags */}
        {selectedDepartments.includes('all') ? (
          <span className="inline-flex items-center px-3 py-1 text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded-full">
            جميع الأقسام
          </span>
        ) : (
          selectedDepartments.slice(0, 3).map(deptId => {
            const dept = departments.find(d => d.id === deptId);
            return dept ? (
              <span key={deptId} className="inline-flex items-center px-3 py-1 text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 rounded-full">
                {dept.name}
              </span>
            ) : null;
          })
        )}
        
        {selectedDepartments.length > 3 && !selectedDepartments.includes('all') && (
          <span className="text-xs text-gray-500 dark:text-gray-400">
            +{selectedDepartments.length - 3} المزيد
          </span>
        )}
      </div>
    </div>
  );
};

export default AnalyticsFilters;
